<script lang="ts">
	import type { HTMLAttributes } from "svelte/elements";
	import { MediaQuery } from "svelte/reactivity";

	const {
		children,
		class: customClass,
		speed = 1,
		flowDirection: direction,
		forceSlidingOnMobile = false,
		forceSlidingOnHover = false,
		forceSlidingOnDesktop = false,
		onEnd,
		endTimeout,
		engine = "scroll",
		...props
	}: HTMLAttributes<HTMLElement> & {
		speed?: number;
		flowDirection: "left" | "right";
		forceSlidingOnMobile?: boolean;
		forceSlidingOnHover?: boolean;
		forceSlidingOnDesktop?: boolean;
		endTimeout?: number;
		engine?: "scroll" | "transform";
		onEnd?(this: void): void;
	} = $props();

	const isDesktop = new MediaQuery("hover: hover", false);
	const directionCoefficient = $derived({ left: 1, right: -1 }[direction]);

	let scrollable = $state(false);

	// scroll engine
	let div = $state<HTMLDivElement>();

	const EPS = 0.25;
	const STALL_MS_TO_END = 160;
	const END_PAUSE_MS = 500;

	function updateScrollableScroll(): void {
		if (div === undefined) {
			return;
		}
		scrollable = forceSlidingOnDesktop || div.scrollWidth > div.clientWidth + 1;
	}

	// engine (iOS-safe)
	let viewport = $state<HTMLDivElement>();
	let track = $state<HTMLDivElement>();
	let viewportW = $state(0);
	let contentW = $state(0);
	let offset = $state(0);
	let isHovered = $state(false);

	const END_TOLERANCE_PX = 0.75;
	const BASE_FRAME_MS = 1000 / 60;
	const MAX_FRAME_DT_MS = 64;

	const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

	const updateMeasurementsTransform = (): void => {
		if (viewport === undefined || track === undefined) {
			return;
		}

		viewportW = viewport.clientWidth;

		const full = track.scrollWidth;
		contentW = onEnd ? full : full / 2;

		scrollable = forceSlidingOnDesktop || contentW > viewportW + 2;
	};

	function handleResize(): void {
		if (engine === "scroll") {
			updateScrollableScroll();
		} else {
			updateMeasurementsTransform();
		}
	}

	$effect(() => {
		handleResize();
	});

	$effect(() => {
		if (typeof window === "undefined") {
			return;
		}

		if (engine !== "transform") {
			return;
		}

		updateMeasurementsTransform();

		const ro = new ResizeObserver(() => {
			updateMeasurementsTransform();
		});

		if (viewport !== undefined) {
			ro.observe(viewport);
		}
		if (track !== undefined) {
			ro.observe(track);
		}

		return (): void => {
			ro.disconnect();
		};
	});

	$effect(() => {
		if (engine !== "transform") {
			return;
		}

		if (!scrollable) {
			offset = 0;
			return;
		}

		const maxOffset = Math.max(0, contentW - viewportW);
		offset = directionCoefficient === 1 ? 0 : maxOffset;
	});

	$effect(() => {
		if (engine === "scroll") {
			let ended = false;
			let stallSince: number | null = null;
			let endDelayId: ReturnType<typeof setTimeout> | undefined;

			const clearEndDelay = (): void => {
				if (endDelayId !== undefined) {
					clearTimeout(endDelayId);
					endDelayId = undefined;
				}
			};

			const resetStall = (): void => {
				stallSince = null;
				ended = false;
				clearEndDelay();
			};

			const fireEnd = (): void => {
				onEnd?.();
				resetStall();
			};

			let animationId = window.requestAnimationFrame(function tick() {
				if (div === undefined) {
					animationId = window.requestAnimationFrame(tick);
					return;
				}

				if (isDesktop.current || !onEnd) {
					const half = div.scrollWidth / 2;
					const delta = div.scrollLeft - half * directionCoefficient;

					if (Math.sign(delta) === directionCoefficient) {
						div.scrollTo({ left: delta / 2 });
					} else if (scrollable && (!isDesktop.current || forceSlidingOnHover || !div.matches(":hover"))) {
						div.scrollLeft += speed * directionCoefficient;
					}

					animationId = window.requestAnimationFrame(tick);
					return;
				}

				const shouldAutoScroll = forceSlidingOnMobile && scrollable;

				if (!shouldAutoScroll) {
					resetStall();
					animationId = window.requestAnimationFrame(tick);
					return;
				}

				const before = div.scrollLeft;
				div.scrollLeft += speed * directionCoefficient;
				const after = div.scrollLeft;

				const moved = Math.abs(after - before) > EPS;

				if (moved) {
					resetStall();
				} else {
					if (stallSince === null) {
						stallSince = Date.now();
					}

					const stalledFor = Date.now() - stallSince;

					if (!ended && stalledFor >= STALL_MS_TO_END) {
						ended = true;

						if (endDelayId === undefined) {
							endDelayId = setTimeout(() => {
								if (ended) {
									fireEnd();
								}
								endDelayId = undefined;
							}, END_PAUSE_MS);
						}
					}
				}

				animationId = window.requestAnimationFrame(tick);
			});

			return (): void => {
				window.cancelAnimationFrame(animationId);
			};
		}

		// transform engine loop
		let endDelayId: ReturnType<typeof setTimeout> | undefined;
		let animationId = 0;
		let lastTs: number | undefined;

		const clearEndDelay = (): void => {
			if (endDelayId !== undefined) {
				clearTimeout(endDelayId);
				endDelayId = undefined;
			}
		};

		const scheduleEnd = (): void => {
			if (endDelayId !== undefined) {
				return;
			}
			endDelayId = setTimeout(() => {
				endDelayId = undefined;
				onEnd?.();
			}, END_PAUSE_MS);
		};

		const shouldAutoScroll = (): boolean => {
			if (!scrollable) {
				return false;
			}
			if (isDesktop.current) {
				if (forceSlidingOnDesktop) {
					return true;
				}
				if (forceSlidingOnHover) {
					return true;
				}
				return !isHovered;
			}
			return forceSlidingOnMobile;
		};

		const tick = (ts: number): void => {
			if (viewport === undefined || track === undefined) {
				animationId = window.requestAnimationFrame(tick);
				return;
			}

			const dt = lastTs === undefined ? BASE_FRAME_MS : Math.min(MAX_FRAME_DT_MS, ts - lastTs);
			lastTs = ts;
			const step = (speed * dt) / BASE_FRAME_MS;

			const endMode = onEnd !== undefined;
			const maxOffset = Math.max(0, contentW - viewportW);

			if (!shouldAutoScroll()) {
				clearEndDelay();
				animationId = window.requestAnimationFrame(tick);
				return;
			}

			if (endMode) {
				const next = clamp(offset + step * directionCoefficient, 0, maxOffset);
				offset = next;

				const atEnd = directionCoefficient === 1 ? next >= maxOffset - END_TOLERANCE_PX : next <= END_TOLERANCE_PX;

				if (atEnd) {
					scheduleEnd();
				} else {
					clearEndDelay();
				}

				animationId = window.requestAnimationFrame(tick);
				return;
			}

			let next = offset + step * directionCoefficient;

			if (directionCoefficient === 1) {
				if (next >= contentW) {
					next -= contentW;
				}
			} else {
				if (next <= 0) {
					next += contentW;
				}
			}

			offset = next;
			animationId = window.requestAnimationFrame(tick);
		};

		animationId = window.requestAnimationFrame(tick);

		return (): void => {
			window.cancelAnimationFrame(animationId);
			clearEndDelay();
		};
	});

	$effect((): (() => void) | undefined => {
		if (!scrollable && endTimeout !== undefined && onEnd !== undefined) {
			const id = setTimeout(() => {
				if (!scrollable) {
					onEnd();
				}
			}, endTimeout);

			return (): void => {
				clearTimeout(id);
			};
		}
		return undefined;
	});

	const translateX = $derived(`translate3d(${-offset}px, 0, 0)`);
</script>

<svelte:window onresize={handleResize} />

{#if engine === "scroll"}
	<div
		{...props}
		class={[
			customClass,
			"flex overflow-x-auto no-scrollbar *:shrink-0",
			{ left: "flex-row", right: "flex-row-reverse" }[direction],
			!scrollable && "justify-center",
		]}
		bind:this={div}
	>
		{@render children?.()}
		{#if scrollable && (isDesktop.current || !onEnd)}
			{@render children?.()}
		{/if}
	</div>
{:else}
	<div
		{...props}
		class={[customClass, "overflow-hidden"]}
		bind:this={viewport}
		onmouseenter={(): void => {
			isHovered = true;
		}}
		onmouseleave={(): void => {
			isHovered = false;
		}}
	>
		<div class="flex *:shrink-0 will-change-transform gap-[inherit]" style:transform={translateX} bind:this={track}>
			{@render children?.()}
			{#if scrollable && onEnd === undefined}
				{@render children?.()}
			{/if}
		</div>
	</div>
{/if}
